import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icons as I } from '../pack-icons';
import { api } from '../../services/api/client.js';
import { platformLabel } from '../../lib/operations.js';

const POLL_MS = 60 * 1000;
// GlobalAssistant (AppShell) listens for this event; detail.context lands in
// AIPanel's notificationContext with the recommendation's proposed action.
const ASSISTANT_OPEN_EVENT = 'dathent:assistant:open';
// Opening the bell asks the server to refresh smart recommendations at most
// this often per tab — the server additionally caps them at 3/day + dedupes.
const RECS_RUN_THROTTLE_MS = 60 * 60 * 1000;
const RECS_RUN_AT_KEY = 'dathent.bell.recsRunAt';

// Notification rows store English title/message; for the types the SPA knows
// about we render localized copy from `metadata` instead, falling back to the
// stored strings for anything else (e.g. smart recommendations).
// "Mon, 12:00" — the moment the top post earned its engagement, shown as the
// proven-time insight on performance recommendations.
function formatBestTime(iso, lang) {
  const ms = Date.parse(iso || '');
  if (!Number.isFinite(ms)) return null;
  try {
    return new Intl.DateTimeFormat(lang, { weekday: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(ms));
  } catch (_) {
    return null;
  }
}

function localizedCopy(n, t, lang) {
  const meta = n.metadata || {};
  if (n.type === 'autopilot_plan') {
    if (meta.limitReached) {
      const count = meta.generatedCount ?? 0;
      let message = count > 0
        ? t('bell.autopilotPlanLimitPartial', { count, requested: meta.requested ?? count })
        : t('bell.autopilotPlanLimitReached');
      // The one thing the owner actually wants to know: WHEN it resumes.
      // resumeAt = the usage period end stamped by the autopilot worker.
      const resumeMs = Date.parse(meta.resumeAt || '');
      if (Number.isFinite(resumeMs) && resumeMs > Date.now()) {
        let date;
        try { date = new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long' }).format(new Date(resumeMs)); } catch (_) { date = meta.resumeAt.slice(0, 10); }
        message += ' ' + t('bell.autopilotPlanLimitResume', { date });
      }
      return { title: t('bell.autopilotPlanTitle'), message };
    }
    return {
      title: t('bell.autopilotPlanTitle'),
      message: t('bell.autopilotPlanMessage', { count: meta.generatedCount ?? '—' }),
    };
  }
  if (n.type === 'autopilot_plan_failed') {
    return { title: t('bell.autopilotFailedTitle'), message: n.message };
  }
  if (n.type === 'autopilot_review_ready') {
    // meta.note is the AI "why it worked" note, already in the brand language;
    // fall back to a localized generic line when the AI note is absent.
    return {
      title: t('bell.autopilotReviewTitle'),
      message: meta.note || t('bell.autopilotReviewGeneric'),
    };
  }
  if (n.type === 'autopilot_plan_tuned') {
    const parts = [];
    if (Array.isArray(meta.topPillars) && meta.topPillars.length) parts.push(meta.topPillars.join(' & '));
    if (meta.bestTime) parts.push(`~${meta.bestTime}`);
    const message = parts.length
      ? t('bell.autopilotTunedSummary', { focus: parts.join(' · ') })
      : t('bell.autopilotTunedGeneric');
    return { title: t('bell.autopilotTunedTitle'), message };
  }
  if (n.type === 'research_ready') {
    return { title: t('bell.researchReadyTitle'), message: n.message || null, cta: t('bell.researchOpenCta') };
  }
  if (n.type === 'research_failed') {
    return { title: t('bell.researchFailedTitle'), message: n.message || null };
  }
  if (n.type === 'first_post_ready') {
    return { title: t('bell.firstPostReadyTitle'), message: n.message || null, cta: t('bell.firstPostOpenCta') };
  }
  if (n.type === 'finish_brand') {
    return {
      title: t('notifications.finish_brandTitle'),
      message: t('notifications.finish_brandMessage'),
    };
  }
  // TASK-050/074 — image retries exhausted: the post is HELD waiting for an
  // image. CTA routes to the post (or Library) where the user can add their
  // own image or explicitly allow a text-only publish.
  if (n.type === 'post_visual_retry_exhausted') {
    return { title: t('bell.visualHeldTitle'), message: t('bell.visualHeldMessage'), cta: t('bell.visualHeldCta') };
  }
  // auth-worker (parallel change): a connected social account's token
  // expired/was revoked. metadata = { platform, accountId, accountLabel }.
  if (n.type === 'social_reconnect_needed') {
    return {
      title: t('bell.socialReconnectTitle', { platform: platformLabel(meta.platform) }),
      message: t('bell.socialReconnectBody'),
    };
  }
  if (n.type === 'plan_rebuilt') {
    const version = meta.researchVersion;
    const message = version
      ? t('bell.planRebuiltMessageVersioned', { version })
      : t('bell.planRebuiltMessage');
    return { title: t('bell.planRebuiltTitle'), message, cta: t('bell.planRebuiltCta') };
  }
  // TASK-331 (M9 fix): publish-dispatcher-worker and autopilot-worker both
  // write type "plan_lapsed" (the shared PLAN_LAPSED_NOTIFICATION_TYPE
  // literal in both repos) for the once-per-org-per-day "paused" bell — the
  // backend's own dedupe means an org gets at most ONE of these a day, and
  // client-side the only way to tell "publishing paused" from "autopilot
  // paused" apart is the metadata shape: blockDispatchForLapsedPlan stamps
  // { publishTargetId, scheduledPostId }; the autopilot skip writes {}.
  // Before this branch these rendered the raw English n.title/n.message
  // (the server-stored strings) for every non-English locale.
  if (n.type === 'plan_lapsed') {
    const isPublishingPause = meta.publishTargetId != null || meta.scheduledPostId != null;
    if (isPublishingPause) {
      // The backend doesn't stamp a paused-post count in metadata yet — `1`
      // is an honest floor (the notification only fires once at least one
      // target was actually blocked that day), not a guess at the real total.
      const count = typeof meta.count === 'number' ? meta.count : 1;
      return { title: t('lapsed.publishingPausedTitle'), message: t('lapsed.publishingPausedBody', { count }) };
    }
    return { title: t('lapsed.autopilotPausedTitle'), message: t('lapsed.autopilotPausedBody') };
  }
  // TASK-331 phase-1-fixes round 3: the renewal sweep now writes this for
  // real — PLAN_RENEWED_NOTIFICATION_TYPE in publish-dispatcher-worker's
  // sweepRenewedOrgs (src/index.ts ~1343, literal "plan_renewed_resume"),
  // metadata { requeued, missed } stamped at ~1507 ("M1 (phase-1 review
  // round 3): metadata keys are exactly `requeued`/`missed` — the SPA
  // fixer's copy aligns to this shape" — that comment is this one). The
  // 'publishing_resumed' literal this branch used to key on was never
  // written by any backend writer (round 2 reserved it speculatively) —
  // renamed to the real type, and the body now surfaces the actual counts
  // instead of a flat, count-less sentence. Falls back to the generic body
  // when metadata is absent/malformed (e.g. an older notification row)
  // rather than interpolating "undefined".
  if (n.type === 'plan_renewed_resume') {
    const requeued = meta.requeued;
    const missed = meta.missed;
    if (typeof requeued === 'number') {
      const message = typeof missed === 'number' && missed > 0
        ? t('lapsed.publishingResumedBodyWithMissed', { requeued, missed })
        : t('lapsed.publishingResumedBody', { requeued });
      return { title: t('lapsed.publishingResumedTitle'), message };
    }
    return { title: t('lapsed.publishingResumedTitle'), message: t('lapsed.publishingResumedBodyGeneric') };
  }
  if (n.type === 'smart_recommendation' || n.type === 'analytics_recommendation') {
    const params = meta.assistantAction?.params || {};
    const quote = typeof params.entityPreview === 'string' && params.entityPreview ? params.entityPreview : null;
    if (meta.recommendationType === 'performance_more_like_this') {
      const bestTime = params.bestTimeIso ? formatBestTime(params.bestTimeIso, lang) : null;
      return {
        title: t('bell.recPerformanceTitle'),
        quote,
        bestTime: bestTime ? t('bell.bestTime', { time: bestTime }) : null,
        cta: t('bell.recPerformanceCta', { count: params.count || 3 }),
      };
    }
    if (meta.recommendationType === 'empty_schedule_next_week') {
      return { title: t('bell.recScheduleTitle'), cta: t('bell.recScheduleCta') };
    }
    if (meta.recommendationType === 'first_post_ready_no_social') {
      return { title: t('bell.recConnectTitle'), quote, cta: t('bell.recConnectCta') };
    }
    return { title: n.title, message: n.message, cta: t('bell.recOpenAssistant') };
  }
  return { title: n.title, message: n.message };
}

// Recommendations open the assistant with the proposed action prefilled —
// the same context shape the legacy notifications panel passed to AIPanel.
// `copy` (the bell's localized rendering) becomes displayText: the visible
// chat message stays human and in the UI language instead of raw inputSummary.
function assistantContextFor(n, copy) {
  const meta = n.metadata || {};
  const action = meta.assistantAction && typeof meta.assistantAction === 'object' ? meta.assistantAction : null;
  const params = action?.params && typeof action.params === 'object' ? action.params : {};
  const intent = typeof meta.assistantIntent === 'string' ? meta.assistantIntent : action?.type;
  if (meta.destination !== 'assistant' && !intent) return null;
  const entityType = typeof meta.entityType === 'string' ? meta.entityType : meta.affectedEntity?.type;
  const entityId = typeof meta.entityId === 'string' ? meta.entityId : meta.affectedEntity?.id;
  const displayBase = copy ? (copy.cta || copy.title) : null;
  return {
    type: 'notification',
    id: n.id,
    notificationId: n.id,
    displayText: displayBase ? (copy.quote ? `${displayBase} — “${copy.quote}”` : displayBase) : null,
    count: typeof params.count === 'number' ? params.count : null,
    recommendationId: typeof meta.recommendationId === 'string' ? meta.recommendationId : null,
    assistantIntent: intent || 'notification.open_recommendation',
    inputSummary: params.inputSummary || meta.reason || n.message || n.title,
    entityType: entityType || null,
    entityId: entityId || null,
    platform: params.platform || (entityType === 'platform' ? entityId : null),
    contentId: params.contentId || (entityType === 'contentItem' ? entityId : null),
    planLengthDays: params.planLengthDays || null,
    cta: typeof meta.cta === 'string' ? meta.cta : null,
    priority: typeof meta.priority === 'string' ? meta.priority : null,
    // creditCost deliberately not forwarded: credits are not a product
    // concept, and the number leaking into the LLM context made it say
    // "this will use 3 credits" in chat.
  };
}

function targetRoute(n) {
  const meta = n.metadata || {};
  if (n.type === 'autopilot_plan') {
    return meta.batchId ? `/app/batch/${encodeURIComponent(meta.batchId)}` : '/app/scheduler';
  }
  if (n.type === 'autopilot_plan_failed') return '/app/dashboard';
  // TASK-331 (M9 fix): both the "publishing paused" and "autopilot paused"
  // bells (shared type 'plan_lapsed') route to Billing, where the renew
  // affordances (BillingStateBanner / Settings) live. Previously these had no
  // targetRoute case, so clicking the notification did nothing.
  if (n.type === 'plan_lapsed' || n.type === 'plan_renewed_resume') return '/app/settings?tab=billing';
  if (n.type === 'research_ready' || n.type === 'research_failed') return '/app/research';
  if (n.type === 'plan_rebuilt') return '/app/plan';
  if (n.type === 'finish_brand') return '/onboarding';
  // The server-driven onboarding chain finished while the user was away —
  // land them on the reveal screen (the wow moment they missed live).
  if (n.type === 'first_post_ready') {
    return meta.contentItemId ? `/app/reveal/${encodeURIComponent(meta.contentItemId)}` : '/app';
  }
  // Held post (image retries exhausted) — open the post preview, where the
  // "Add image" / "Publish without image" actions live; Library as fallback.
  if (n.type === 'post_visual_retry_exhausted') {
    const contentId = meta.contentItemId || meta.contentId || (meta.entityType === 'contentItem' ? meta.entityId : null);
    return contentId ? `/app/post/${encodeURIComponent(contentId)}` : '/app/library';
  }
  // Deep-links straight to that platform's card on the Accounts tab (see
  // Settings.jsx's `?focus=<platform>` handling, mobile shell fix 2026-09).
  if (n.type === 'social_reconnect_needed') {
    const platform = meta.platform ? encodeURIComponent(String(meta.platform).toLowerCase()) : null;
    return platform ? `/app/settings?tab=accounts&focus=${platform}` : '/app/settings?tab=accounts';
  }
  return null;
}

function timeAgo(iso, lang, justNow) {
  const ts = Date.parse(iso || '');
  if (!Number.isFinite(ts)) return '';
  const diffSec = Math.round((ts - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return justNow || '';
  try {
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
    if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
    if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
    return rtf.format(Math.round(diffSec / 86400), 'day');
  } catch (_) {
    return '';
  }
}

// Per-type leading icon for the bell rows (ported from research-07-notifications):
// a 30px tinted circle — ok (success) / fail (danger) / info (accent) — with a
// glyph that matches the event. Unknown types fall back to the sparkle (recs).
function iconFor(type) {
  switch (type) {
    case 'first_post_ready':
      return { v: 'ok', d: ['M12 3 13.5 8 18 9.5 13.5 11 12 16 10.5 11 6 9.5 10.5 8z'] };
    case 'research_ready':
      return { v: 'ok', d: ['m5 12 5 5 9-11'] };
    case 'research_failed':
    case 'autopilot_plan_failed':
      return { v: 'fail', d: ['M12 2 2 21h20z', 'M12 9v5', 'M12 17.5v.5'] };
    case 'post_visual_retry_exhausted':
      // Image frame with a "mountain" — the post is waiting for its image.
      return { v: 'fail', d: ['M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z', 'm21 15-5-5L5 21', 'M9 9h.01'] };
    case 'strategy_updated':
    case 'strategy_refreshed':
    case 'plan_rebuilt':
      return { v: 'info', d: ['M21 12a9 9 0 1 1-3-6.7', 'M21 4v5h-5'] };
    case 'plan_renewed_resume':
    case 'publishing_paused':
    // TASK-331 (M9 fix): the actual backend literal for both the "publishing
    // paused" and "autopilot paused" bells is 'plan_lapsed' (see
    // PLAN_LAPSED_NOTIFICATION_TYPE in publish-dispatcher-worker and
    // autopilot-worker) — 'publishing_paused' above was never a real type any
    // writer emits. Routed to the same icon this case was clearly prepared for.
    // 'plan_renewed_resume' (round 3) is the real renewal-sweep type — see
    // localizedCopy above for the sweepRenewedOrgs file:line citation.
    case 'plan_lapsed':
      return { v: 'info', d: ['m22 2-11 11', 'M22 2l-7 20-4-9-9-4z'] };
    case 'autopilot_plan':
      return { v: 'info', d: ['M8 2v4', 'M16 2v4', 'M3 10h18', 'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z'] };
    case 'social_reconnect_needed':
      // Same refresh glyph as pack-icons' `refresh` — a broken connection
      // that needs a reconnect action, not a hard failure.
      return { v: 'fail', d: ['M3 12a9 9 0 0 1 15-6.7L21 8', 'M21 3v5h-5', 'M21 12a9 9 0 0 1-15 6.7L3 16', 'M3 21v-5h5'] };
    default:
      return { v: 'info', d: ['M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5z'] };
  }
}

export default function NotificationBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null); // null = list not fetched yet
  const ref = useRef(null);

  const refreshUnread = useCallback(async () => {
    try {
      const res = await api.notifications.getUnreadCount();
      const data = res?.data ?? res;
      if (typeof data?.unreadCount === 'number') setUnread(data.unreadCount);
    } catch (_) {
      // Badge is best-effort; the next poll retries.
    }
  }, []);

  useEffect(() => {
    refreshUnread();
    const id = setInterval(refreshUnread, POLL_MS);
    const onFocus = () => refreshUnread();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshUnread]);

  // Close on outside click / Escape (same idiom as ActivityPill).
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const isUnread = (n) => (n.status ? n.status === 'unread' : !n.read);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next) return;
    try {
      // Refresh smart recommendations so fresh ones land in this very open —
      // at most once an hour per tab (the server adds a daily cap + dedupe).
      let lastRunAt = 0;
      try { lastRunAt = Number(window.sessionStorage.getItem(RECS_RUN_AT_KEY)) || 0; } catch (_) { /* ignore */ }
      if (Date.now() - lastRunAt > RECS_RUN_THROTTLE_MS) {
        try { window.sessionStorage.setItem(RECS_RUN_AT_KEY, String(Date.now())); } catch (_) { /* ignore */ }
        await api.notifications.runSmartRecommendations().catch(() => {});
      }
      const res = await api.notifications.list({ limit: 20 });
      const data = res?.data ?? res;
      setItems(Array.isArray(data?.notifications) ? data.notifications : []);
      if (typeof data?.unreadCount === 'number') setUnread(data.unreadCount);
    } catch (_) {
      setItems([]);
    }
  };

  const onItemClick = (n) => {
    if (isUnread(n)) {
      setItems((prev) => prev?.map((x) => (x.id === n.id ? { ...x, status: 'read', read: true } : x)) ?? prev);
      setUnread((u) => Math.max(0, u - 1));
      api.notifications.markRead(n.id).catch(() => {});
    }
    const assistantContext = assistantContextFor(n, localizedCopy(n, t, i18n.language));
    if (assistantContext) {
      setOpen(false);
      window.dispatchEvent(new CustomEvent(ASSISTANT_OPEN_EVENT, { detail: { context: assistantContext } }));
      return;
    }
    const route = targetRoute(n);
    if (route) {
      setOpen(false);
      navigate(route);
    }
  };

  const onReadAll = () => {
    setItems((prev) => prev?.map((x) => ({ ...x, status: 'read', read: true })) ?? prev);
    setUnread(0);
    api.notifications.markAllRead().catch(() => {});
  };

  return (
    <div className={`nbell ${open ? 'is-open' : ''}`} ref={ref}>
      <button
        className="btn btn-ghost btn-sm btn-icon nbell-btn"
        type="button"
        title={t('bell.title')}
        aria-label={unread > 0 ? t('bell.openUnreadLabel', { count: unread }) : t('bell.title')}
        aria-expanded={open ? 'true' : 'false'}
        onClick={toggle}
      >
        <I.bell size={14} />
        {unread > 0 && (
          <span className="nbell-badge" aria-hidden>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      <div className="nbell-panel" role="region" aria-label={t('bell.title')}>
        <div className="nbell-h">
          <span className="nbell-title">{t('bell.title')}</span>
          {unread > 0 && (
            <button className="nbell-readall" type="button" onClick={onReadAll}>
              {t('bell.markAllRead')}
            </button>
          )}
        </div>
        {!items ? (
          <div className="nbell-empty"><span className="dot pulse" /></div>
        ) : items.length === 0 ? (
          <div className="nbell-empty">
            <I.bell size={15} />
            <span>{t('bell.empty')}</span>
          </div>
        ) : (
          <ul className="nbell-list">
            {items.map((n) => {
              const copy = localizedCopy(n, t, i18n.language);
              const meta = n.metadata || {};
              const ic = iconFor(n.type);
              const isRec = n.type === 'smart_recommendation' || n.type === 'analytics_recommendation';
              // Recommendations are evergreen suggestions manufactured when the
              // bell opens — their creation time reads as "the post happened
              // X minutes ago" and only confuses, so it's hidden for them.
              const viewPostId = isRec
                ? (meta.assistantAction?.params?.contentId
                  || (meta.entityType === 'contentItem' ? meta.entityId : null)
                  || (meta.affectedEntity?.type === 'contentItem' ? meta.affectedEntity.id : null))
                : null;
              const openPost = (e) => {
                e.stopPropagation();
                setOpen(false);
                navigate(`/app/post/${encodeURIComponent(viewPostId)}`);
              };
              return (
                <li key={n.id}>
                  <button
                    className={`nbell-item ${isUnread(n) ? 'is-unread' : ''}`}
                    type="button"
                    onClick={() => onItemClick(n)}
                  >
                    <span className={`nbell-item-ico is-${ic.v}`} aria-hidden>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {ic.d.map((d, di) => <path key={di} d={d} />)}
                      </svg>
                    </span>
                    <span className="nbell-item-body">
                      <span className="nbell-item-t">{copy.title}</span>
                      {copy.quote && <span className="nbell-item-q">“{copy.quote}”</span>}
                      {copy.bestTime && <span className="nbell-item-bt">{copy.bestTime}</span>}
                      {copy.message && <span className="nbell-item-m">{copy.message}</span>}
                      {(copy.cta || viewPostId) && (
                        <span className="nbell-item-actions">
                          {copy.cta && <span className="nbell-item-cta">{copy.cta} <I.arrowRight size={10} /></span>}
                          {viewPostId && (
                            <span
                              className="nbell-item-view"
                              role="link"
                              tabIndex={0}
                              onClick={openPost}
                              onKeyDown={(e) => { if (e.key === 'Enter') openPost(e); }}
                            >
                              {t('bell.viewPost')}
                            </span>
                          )}
                        </span>
                      )}
                      {!isRec && (
                        <span className="nbell-item-time">{timeAgo(n.createdAt || n.created_at, i18n.language, t('bell.justNow'))}</span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
